import { getCurrentScope, onScopeDispose, useSSRContext } from 'vue';
import type { JsonLdSchema, StructuredDataEntry, UseJsonLdOptions } from './types';

const SCRIPT_TAG = 'script';
const JSON_LD_TYPE = 'application/ld+json';

/**
 * Attribute that tags a JSON-LD node with an explicit deduplication id.
 * The SSR renderer emits the same attribute, which is how the client adopts a
 * server-rendered node by id — including to enrich it — instead of inserting a
 * second copy during hydration. Nodes deduplicated by content carry no id.
 */
export const JSON_LD_ID_ATTRIBUTE = 'data-json-ld';

/**
 * Serializes a schema for embedding inside a `<script>` element.
 *
 * Unicode-escapes `<`, `>` and `&` so content cannot terminate the surrounding
 * `<script>` element. An HTML tokenizer ends a script at the first literal
 * `</script` sequence regardless of JavaScript string quoting, so escaping is
 * the only thing preventing authored content from breaking out of the tag.
 *
 * The client path in {@link useJsonLd} also serializes through this — not for
 * safety (DOM-created nodes are never re-parsed) but so a client node is
 * byte-identical to the server's, which is what makes content deduplication match.
 *
 * Mirrored in `@seamless/oneweb-ssr` (`src/render/escape.ts`) rather than shared:
 * that package inlines into consumer builds and is not a runtime dependency of the
 * components that use it. Keep the two implementations in step.
 */
export function serializeJsonLd(schema: JsonLdSchema): string {
  return JSON.stringify(schema)
    .replaceAll('<', String.raw`\u003C`)
    .replaceAll('>', String.raw`\u003E`)
    .replaceAll('&', String.raw`\u0026`);
}

/**
 * Finds a JSON-LD node already in the document that this declaration should adopt.
 *
 * With an explicit `id`, matching is by the {@link JSON_LD_ID_ATTRIBUTE} so the
 * server and client agree on one node even if their schemas differ. Without one,
 * matching is by serialized content, so two identical payloads — including the
 * server-rendered copy in the body during hydration — collapse to a single node.
 */
function findAdoptableNode(id: string | undefined, serialized: string): Element | undefined {
  const nodes = document.querySelectorAll(`${SCRIPT_TAG}[type="${JSON_LD_TYPE}"]`);
  for (const node of nodes) {
    const matches = id ? node.getAttribute(JSON_LD_ID_ATTRIBUTE) === id : node.textContent === serialized;
    if (matches) {
      return node;
    }
  }
  return undefined;
}

/**
 * Declares a JSON-LD structured data payload for the current component.
 *
 * Call it in `setup`; nothing is added to the template. This is a Vue composable —
 * it must run synchronously inside a component's `setup()` (or another composable
 * called from it), like `onMounted` or `inject`. It relies on Vue's
 * `getCurrentScope`/`onScopeDispose` for client-side cleanup and on `useSSRContext`
 * for the server branch, so it only works for Vue components: not from a loader
 * (use `addStructuredData` there instead), a plain function, or a non-Vue integration.
 *
 * On the server the payload is registered on the SSR context for the renderer to
 * emit into the body. On the
 * client — which is the whole story for components that never run through the
 * OneWeb SSR renderer — it becomes a `<script type="application/ld+json">` in
 * `document.head`.
 *
 * Duplicates are avoided by content: if an identical node is already present
 * (e.g. the server-rendered copy during hydration, or another component declaring
 * the same schema), this adopts it instead of inserting a second one. Pass an
 * explicit `id` to instead key on a stable identifier — both sides can then share
 * one node even when their schemas differ, and the client's schema is written onto
 * the shared node so a component can enrich what the server rendered.
 *
 * The schema is read once. Structured data is derived from content already settled
 * by render time and is only ever read by crawlers as a snapshot, so there is no
 * reactive tracking: pass the resolved schema, or `undefined` while it is still
 * loading and call again once it is ready.
 *
 * Unlike `updateMetaTags`, this is not gated on the page's main routing namespace:
 * several components on one page may each contribute a distinct node.
 *
 * @param schema The schema.org node, or `undefined` when there is nothing to declare.
 * @param options Pass an explicit `id` to key deduplication on an identifier rather
 *   than the schema contents. It is a shared merge key: declarations collapse into one
 *   node only when they carry the same `id` (use it to enrich, on the client, a node a
 *   loader rendered on the server). An `id` on only one side never merges and can
 *   duplicate; leave it off for independent, content-deduped nodes.
 */
export function useJsonLd(schema: JsonLdSchema | undefined, options: UseJsonLdOptions = {}): void {
  if (!schema) {
    return;
  }

  const { id } = options;
  const serialized = serializeJsonLd(schema);

  if (typeof window === 'undefined') {
    const context = useSSRContext<{ structuredData?: StructuredDataEntry[] }>();
    if (context) {
      const entry: StructuredDataEntry = id ? { id, schema } : { schema };
      context.structuredData = [...(context.structuredData ?? []), entry];
    }
    return;
  }

  const adopted = findAdoptableNode(id, serialized);
  if (adopted) {
    // A shared id can point at a node whose schema differs from this one (the client
    // enriching what the server rendered); write the current schema onto it. Content
    // matches are already identical, so there is nothing to update.
    if (id && adopted.textContent !== serialized) {
      adopted.textContent = serialized;
    }
    return;
  }

  const element = document.createElement(SCRIPT_TAG);
  element.setAttribute('type', JSON_LD_TYPE);
  if (id) {
    element.setAttribute(JSON_LD_ID_ATTRIBUTE, id);
  }
  element.textContent = serialized;
  document.head.appendChild(element);

  // Remove the node when the declaring scope goes away (e.g. SPA navigation), so a
  // page never keeps structured data for a component it no longer renders. Only the
  // call that created the node removes it; adopted nodes are left to their owner.
  if (getCurrentScope()) {
    onScopeDispose(() => {
      element.remove();
    });
  }
}
